import { useModal } from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback } from 'react'
import { generatePath, useNavigate } from 'react-router'

import { Data, PolicyAdjustmentResponse } from '../../../../../domain'
import { useFormValidation } from '../../../../../hooks/adjustments/formValidation'
import {
  usePolicyAdjustment,
  usePolicyFormData,
} from '../../../../../hooks/adjustments/policy'
import {
  TExtends,
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { useUpButton } from '../../../../../infrastructure/routing'
import { QuoteManagementConfig } from '../../../../../state/configuration'
import { usePrincipal } from '../../../../authentication'
import { FormErrors } from '../../../../formBuilder'
import { FullPage } from '../../../../templates'
import { usePolicy } from '../../PolicyProvider'
import { PolicyAdjustmentModal } from './PolicyAdjustmentModal'
import { PolicyAdjustmentPath } from './PolicyAdjustmentPath'

const tKey = TKey('pages.disclosure')

export interface PolicyAdjustmentPageProps {
  config: QuoteManagementConfig
}

export const PolicyAdjustmentPage: FunctionComponent<PolicyAdjustmentPageProps> = ({
  config,
}) => {
  const { t } = useWriskTranslation()
  const { policy } = usePolicy()
  const { isAdmin } = usePrincipal()
  const modal = useModal()

  const data = usePolicyFormData(config.form)
  const navigate = useNavigate()

  const upButton = useUpButton({ url: '..', id: 'management' })

  const onSuccess = useCallback(
    (response: PolicyAdjustmentResponse | undefined) => {
      modal.hide()

      response
        ? navigate(
            generatePath(PolicyAdjustmentPath.Adjustment, {
              adjustmentId: response.adjustmentId,
            }),
          )
        : navigate('..')
    },
    [modal, navigate],
  )

  const onSubmitAdjustment = usePolicyAdjustment({
    form: config.form,
    onSubmitted: onSuccess,
  })

  const onValidateForm = useFormValidation({
    validationProfile: config.validationProfile,
    onSuccess: async (updatedData: Data) => {
      if (isAdmin) {
        modal.show({
          content: (
            <PolicyAdjustmentModal
              updatedData={updatedData}
              policy={policy}
              config={config}
              onSuccess={onSuccess}
              onCancel={modal.hide}
            />
          ),
        })
      } else {
        await onSubmitAdjustment.execute(updatedData)
      }
    },
  })

  return (
    <FullPage
      pageId='adjustment-section'
      header={t(tKey('headers', config.id))}
      upButton={upButton}
    >
      <FormErrors
        id='adjustmentSection'
        data={data}
        config={config.form}
        onSubmit={onValidateForm.execute}
        loading={onValidateForm.loading}
        tKey={TExtends(tKey, 'form')}
        t={t}
      />
    </FullPage>
  )
}
